// Settings, opened from the gear on every tab: a short menu in three groups (Capture, Household,
// Account), each row with a status on the right and its own screen. Back from a screen returns to
// the menu where it was; Back from the menu returns to the tab Settings was opened from.
import type { OverviewDto } from '../../../shared/api';
import { esc } from '../format';
import { detailBar } from './header';
import { SHARE_BANKS, SHARE_BANKS_LABEL } from '../../../shared/setup-copy';
import { settleLink } from './settle';
import { PARTNER_CODE_HINT, PARTNER_INTRO, PARTNER_INVITE_DAYS, PARTNER_INVITE_HOURS, PARTNER_UNLINK_HINT, joinedLine, partnerLine } from '../../../shared/partner';

/** Settings, Partner: the options are open, and the code just made (kept in memory only, until the app reloads). */
export interface PartnerUi {
  open: boolean;
  code: string | null;
  expiresAt: string | null;
}

/** "Tiffany joined and is linked" shows for this long after an emailed invite was used. */
const JOINED_DAYS = 14;

/**
 * Partner: "Invite your partner" (by email, the main path; a code below it), or "Partner: Tiffany
 * (linked)" with Unlink ("Tiffany joined and is linked" for a while after an emailed invite was
 * used). Pending email invites show with Cancel. Linked, or with a balance left after an unlink, "Splits and settle up" opens the Splits screen.
 */
export function vPartner(d: OverviewDto, p: PartnerUi = { open: false, code: null, expiresAt: null }, now = Date.now()): string {
  const linked = d.partner.linked;
  if (linked) {
    const fresh = linked.joined && now - Date.parse(linked.since) < JOINED_DAYS * 86_400_000;
    return `<div class="brow"><span>${esc(fresh ? joinedLine(linked.name) : partnerLine(linked.name))}</span><button class="btn" data-a="partner-unlink">Unlink</button></div>
  <p class="hint">${esc(PARTNER_UNLINK_HINT)}</p>${settleLink(d)}`;
  }
  // After an unlink, a balance left over stays viewable.
  const left = settleLink(d);
  const pending = left + (d.partner.emailInvites.length
    ? `<div class="list">${d.partner.emailInvites
        .map(
          (i) =>
            `<div class="brow"><span>Invite${i.name ? ` to ${esc(i.name)}` : ''} sent ${esc(new Date(i.createdAt).toLocaleDateString())}<br><small class="muted">Works until ${esc(new Date(i.expiresAt).toLocaleDateString())}</small></span><button class="btn" data-a="partner-invite-cancel" data-id="${esc(i.id)}">Cancel</button></div>`,
        )
        .join('')}</div>`
    : '');
  if (!p.open && !p.code) return `${pending}<div class="btns"><button class="btn" data-a="partner-open">Invite your partner</button></div>`;
  const byEmail = d.features.emailSignIn
    ? `<p class="hint">Hush emails them a link to sign up. It works once, for ${PARTNER_INVITE_DAYS} days, and only with that email. You are linked as soon as they join.</p>
  <label class="fld"><span>Partner's email</span><input id="partner-email" type="email" autocomplete="off" placeholder="partner@example.com"></label>
  <label class="fld"><span>Their first name (optional)</span><input id="partner-name" type="text" maxlength="60" autocomplete="off"></label>
  <div class="btns"><button class="btn primary" data-a="partner-invite-send">Send invite</button></div>
  <p class="hint">Already use Hush, both of you? Link with a code instead:</p>`
    : '';
  const code = p.code
    ? `<p class="code" aria-label="Your partner code">${esc(p.code)}</p><p class="hint">${esc(PARTNER_CODE_HINT(PARTNER_INVITE_HOURS))}</p>`
    : d.partner.invite
      ? `<p class="hint">Your code works until ${esc(new Date(d.partner.invite.expiresAt).toLocaleString())}. Lost it? Make a new one; the old one stops working.</p>`
      : '';
  return `<p class="hint">${esc(PARTNER_INTRO)}</p>
  ${pending}
  ${byEmail}
  ${code}
  <div class="btns"><button class="btn" data-a="partner-invite">${p.code || d.partner.invite ? 'Make a new code' : 'Make a code for my partner'}</button></div>
  <p class="hint">Or enter the code your partner made:</p>
  <div class="addcat"><input id="partner-code" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="ABCD-EFGH" aria-label="Partner code" maxlength="20"><button class="btn" data-a="partner-accept">Link</button></div>`;
}

/** An emailed invite opened while signed in: link with the person who sent it. */
export function vUseInviteSheet(inviterName: string, alreadyLinked: boolean): string {
  return `<div class="grab"></div><h2>Link with ${esc(inviterName)}?</h2>
  ${
    alreadyLinked
      ? `<p class="hint">You already have a partner linked. Unlink in Settings, Partner first, then open the invite link again.</p><div class="btns"><button class="btn" data-a="close">Close</button></div>`
      : `<p class="hint">${esc(inviterName)} invited you. Your budgets, categories and cards stay separate; linking lets you split shared costs.</p>
  <div class="btns"><button class="btn primary" data-a="partner-invite-use">Link</button><button class="btn" data-a="close">Not now</button></div>`
  }`;
}

/** Settings pages, each opened from one row of the menu. */
export type SettingsPage = 'cards' | 'mode' | 'learned' | 'partner' | 'account' | 'notifications' | 'privacy' | 'data' | 'tools';

export interface MenuRow {
  page: SettingsPage;
  label: string;
  /** The status on the right of the row. */
  status: string;
  /** Amber when something needs the user. */
  warn?: boolean;
}

/** The menu: three groups of rows, each with a status. Tests pin this order. */
/** `pushStatus`: the Notifications row's status (from this device; '' while it loads). */
export function settingsMenu(d: OverviewDto, pushStatus = ''): { group: string; rows: MenuRow[] }[] {
  const f = d.features;
  const cb = d.cardsBanks;
  const attention = cb.banks.some((b) => b.status === 'attention' || b.cards.some((c) => c.status === 'attention'));
  const capture: MenuRow[] = [
    f.inboundEmail
      ? { page: 'cards', label: 'Cards and banks', status: cb.summary, warn: attention }
      : { page: 'cards', label: 'Cards and banks', status: 'Off on this server' },
    { page: 'mode', label: 'Mode', status: d.user.mode === 'quiet' ? 'Stay quiet' : 'Ask me' },
    { page: 'learned', label: 'What Hush has learned', status: 'Change or forget' },
  ];
  if (f.pasteIntake || f.sampleData) capture.push({ page: 'tools', label: 'Test tools', status: d.isSample ? 'Sample data on' : 'Test mode' });
  const household: MenuRow[] = d.isSample
    ? []
    : [{ page: 'partner', label: 'Partner and splits', status: d.partner.linked ? `Linked with ${d.partner.linked.name}` : d.partner.balanceLeft ? 'Balance left' : 'Not linked' }];
  const account: MenuRow[] = [
    {
      page: 'account',
      label: 'Sign-in and recovery',
      status: d.user.email ? d.user.email : d.user.hasRecoveryCode ? 'Recovery code' : 'No way back in',
      warn: !d.user.email && !d.user.hasRecoveryCode,
    },
    // Without VAPID on the server it is email only; else this device's state ('' while it loads).
    ...(d.isSample ? [] : [{ page: 'notifications' as const, label: 'Notifications', status: f.push ? pushStatus : 'Email only' }]),
    { page: 'privacy', label: 'Privacy', status: d.household.shareBanks ? 'Sharing banks' : 'Not sharing' },
    { page: 'data', label: 'Your data', status: 'Export or delete' },
  ];
  return [
    { group: 'Capture', rows: capture },
    ...(household.length ? [{ group: 'Household', rows: household }] : []),
    { group: 'Account', rows: account },
  ];
}

/** Settings: a short menu. Each row opens its own screen; Back comes back here. */
export function vSettings(d: OverviewDto, pushStatus = ''): string {
  const groups = settingsMenu(d, pushStatus)
    .map(
      (g) => `<h2>${esc(g.group)}</h2><div class="list menu">${g.rows
        .map(
          (r) =>
            `<button class="mrow" data-a="settings-page" data-page="${r.page}"><span>${esc(r.label)}</span><span class="mstat${r.warn ? ' warn' : ''}">${esc(r.status)}</span></button>`,
        )
        .join('')}</div>`,
    )
    .join('');
  return `${detailBar('Settings', 'Set it up once, then leave it')}${groups}<button class="reset" data-a="signout">Sign out</button>`;
}

/** Page titles. */
export const PAGE_TITLES: Record<Exclude<SettingsPage, 'cards' | 'learned' | 'notifications'>, string> = {
  mode: 'Mode',
  partner: 'Partner and splits',
  account: 'Sign-in and recovery',
  privacy: 'Privacy',
  data: 'Your data',
  tools: 'Test tools',
};

/** One of the simple pages (Cards and banks and What Hush has learned have their own views). */
export function vSettingsPage(d: OverviewDto, page: Exclude<SettingsPage, 'cards' | 'learned' | 'notifications'>, partner?: PartnerUi): string {
  const f = d.features;
  const head = detailBar(PAGE_TITLES[page], '');
  switch (page) {
    case 'mode': {
      const mode = d.user.mode;
      return `${head}<div class="optlist">
    <button class="opt" data-a="mode" data-mode="ask" aria-pressed="${mode === 'ask'}"><b>Ask me</b><span>New purchases wait on Overview so you can check how Hush filed them.</span></button>
    <button class="opt" data-a="mode" data-mode="quiet" aria-pressed="${mode === 'quiet'}"><b>Stay quiet</b><span>Everything files itself. One digest on Sundays, plus an alert when a budget drifts.</span></button>
  </div>`;
    }
    case 'partner':
      return `${head}${vPartner(d, partner)}`;
    case 'account': {
      const email = !f.emailSignIn
        ? ''
        : d.user.email
          ? `<p class="hint">You sign in with ${esc(d.user.email)}. To use Hush on another device, open it there and choose Sign in with email.</p>`
          : `<p class="hint">Add an email so you can sign in on another device or get back in if you are signed out.</p>
  <div class="addcat"><input id="signin-email" type="email" autocomplete="email" placeholder="you@example.com" aria-label="Sign-in email"><button class="btn" data-a="set-email">Send link</button></div>`;
      const recovery = `<h2>Recovery code</h2><p class="hint">${
        d.user.hasRecoveryCode
          ? 'Your recovery code signs you in on a new device. It works once. Lost it? Make a new one. The old one stops working.'
          : 'You have no recovery code. Make one so you can get back in on a new device.'
      }</p><div class="btns"><button class="btn" data-a="recovery-new">Make a new recovery code</button></div>`;
      const canGetBackIn = !!d.user.email || d.user.hasRecoveryCode;
      const signoutAll = canGetBackIn
        ? `<h2>Sign out of all devices</h2><p class="hint">Signs Hush out on every phone and computer, this one too.</p><div class="btns"><button class="btn danger" data-a="signout-all">Sign out of all devices</button></div>`
        : `<h2>Sign out of all devices</h2><p class="hint">Make a recovery code first, so you can get back in.</p>`;
      return `${head}${email}${recovery}${signoutAll}`;
    }
    case 'privacy':
      return `${head}<label class="check"><input type="checkbox" data-share ${d.household.shareBanks ? 'checked' : ''}> <span><b>${esc(SHARE_BANKS_LABEL)}</b><br><span class="hint">${esc(SHARE_BANKS)}</span></span></label>`;
    case 'data':
      return `${head}<p class="hint">Download this month as a spreadsheet, for example to compare with another budgeting app.</p>
  <div class="btns"><button class="btn" data-a="export" data-kind="transactions">Transactions CSV</button><button class="btn" data-a="export" data-kind="categories">Category totals CSV</button></div>
  <p class="hint">Everything Hush keeps for you and your household, in one file.</p>
  <div class="btns"><button class="btn" data-a="export-all">Download all my data</button></div>
  <p class="hint">${esc(deleteHint(d))}</p>
  <div class="btns"><button class="btn danger" data-a="delete-account">Delete my account and all data</button></div>`;
    case 'tools': {
      const tools: string[] = [];
      if (f.pasteIntake)
        tools.push(`<p class="hint">Test mode: copy a card alert email from your bank and paste it here. Hush reads it the same way as a forwarded alert. Pasted alerts never make a card Working.</p>
  <div class="btns"><button class="btn" data-a="paste-open">Paste an alert</button></div>`);
      if (f.sampleData)
        tools.push(
          d.isSample
            ? `<p class="hint">You are looking at sample data. Nothing here is yours.</p><div class="btns"><button class="btn" data-a="sample-leave">Leave sample data</button></div>`
            : `<p class="hint">Preview what Hush shows after a few months of use, without touching your budget.</p><div class="btns"><button class="btn" data-a="sample-load">Load sample data</button></div>`,
        );
      return `${head}${tools.join('')}`;
    }
  }
}

/** What deleting removes: the whole household for its last member, else this person only. */
export function deleteHint(d: OverviewDto): string {
  return (d.household.members ?? 1) > 1
    ? 'Deletes your account and your sign-in details. The shared budget stays with the rest of your household.'
    : 'Deletes your account, your budget and every purchase. This cannot be undone.';
}

/** The confirmation sheet: type "delete" to enable the button. */
export function vDeleteAccountSheet(d: OverviewDto): string {
  return `<div class="grab"></div><h2>Delete your account?</h2>
  <p>${esc(deleteHint(d))} Download your data first if you want a copy.</p>
  <label class="fld"><span>Type delete to confirm</span><input id="del-confirm" type="text" autocomplete="off" autocapitalize="none" spellcheck="false"></label>
  <p class="err" id="del-err"></p>
  <div class="btns"><button class="btn danger" data-a="delete-account-confirm" disabled>Delete my account</button><button class="btn" data-a="close">Keep my account</button></div>`;
}
